import { t } from '@/utils/i18n'
import { useState, useRef, useEffect, useMemo } from 'react'
import { useMount } from 'ahooks'
import styles from './index.module.less'
import { Input, Button, NavBar, Toast, Checkbox } from 'antd-mobile'
import { useNavigate } from 'react-router-dom'
import Gap from '@/components/Gap'
import ShippingTypeLabel from '@/components/ShippingTypeLabel'
import OrderAddress from '../components/OrderAddress'
import { useCorpSettingStore, useCorpInfoStore, useCartStore } from '@/stores'
import ConfirmProductItem from '../components/OrderDetailItem'
import ShippingOption from '../components/ShippingOption'
import type { OrderPickType } from '@/pages/Order/types'
import { constant, apis, utils, cal, routesMap, uploadEt } from '@/utils'
import { ProductItem } from '@/types/product'
import { CustomerAddress } from '@/pages/Order/types'
import eventUtil from '@/utils/event'
import useGetLogistics from '@/pages/Order/hooks/useGetLogistics'

const {
  order: { ShipType, PickType },
} = constant
const { orderApi } = apis
const { to, px2rem, isNumber } = utils
const { moneyFormat } = cal
const SHOW_VAT = false; // 暂时隐藏 VAT

interface VatItem {
  [key: string]: any
}

function ConfirmOrder() {
  const { corpSetting } = useCorpSettingStore()
  const { corpInfo } = useCorpInfoStore()
  const { cart, setCart } = useCartStore()
  const navigate = useNavigate()
  const { currencySymbol } = corpSetting
  const { corpId, pfUserId } = corpInfo
  const { cartId } = cart
  const orderAddressRef = useRef<any>(null)
  const [domesticProducts, setDomesticProducts] = useState<ProductItem[]>([]) // 本土订单
  const [overseasProducts, setOverseasProducts] = useState<ProductItem[]>([]) // 跨海订单
  const [domesticsAmount, setDomesticsAmount] = useState(0)
  const [overseasAmount, setOverseasAmount] = useState(0)
  const [domesticMessage, setDomesticMessage] = useState('')
  const [overseasMessage, setOverseasMessage] = useState('')
  const [domesticShipping, setDomesticShipping] = useState<OrderPickType>({} as OrderPickType)
  const [overseaShipping, setOverseaShipping] = useState<OrderPickType>({} as OrderPickType)
  const [customer, setCustomer] = useState<CustomerAddress>({
    name: '',
    phone: '',
    area: [],
    address: '',
    totalAddress: '',
  })
  const [summary, setSummary] = useState({
    amount: 0, // 货款
    total: 0, // 加上运费后的应收
    shippingFee: 0,
    codFee: 0,
    vatTotal: 0,
  })
  const [saveLoading, setSaveLoading] = useState(false)
  const { getFirstValidLogistics, extend } = useGetLogistics()
  const { rate } = extend // 税率
  const [vatList, setVatList] = useState<VatItem>({})

  useMount(() => {
    getCheckoutOrder()
  })

  useEffect(() => {
    handleFeeChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domesticShipping, overseaShipping, vatList])

  useEffect(() => {
    eventUtil.on('shippingOptionChange', onShippingOptionChange)
    return () => {
      eventUtil.off('shippingOptionChange', onShippingOptionChange)
    }
  }, [])

  // 是否全是自提
  const isAllSelfPick = useMemo(() => {
    return (domesticShipping.pickType === PickType.SELF_PICK || domesticShipping.pickType === undefined) &&
      (overseaShipping.pickType === PickType.SELF_PICK || overseaShipping.pickType === undefined)
  }, [domesticShipping, overseaShipping])

  // 获取确认订单数据
  async function getCheckoutOrder() {
    const [err, res] = await to(
      orderApi.shoppingCartCheckout({
        cartId,
        corpId,
      })
    )

    if (err || !res) {
      return
    }

    const resData = res.data || {}
    const {
      amount = 0,
      domesticsProductAmount,
      domesticsProducts,
      overseasProductAmount,
      overseasProducts,
    } = resData
    setDomesticsAmount(domesticsProductAmount)
    setOverseasAmount(overseasProductAmount)
    setDomesticProducts(domesticsProducts)
    setOverseasProducts(overseasProducts)
    if (domesticsProducts?.length) {
      const firstValidLogistics = await getFirstValidLogistics({ crossBorder: 0 })
      !!firstValidLogistics && setDomesticShipping(firstValidLogistics)
    }
    if (overseasProducts?.length) {
      const firstValidLogistics = await getFirstValidLogistics({ crossBorder: 1 })
      !!firstValidLogistics && setOverseaShipping(firstValidLogistics)
    }

    setSummary({
      ...summary,
      amount,
    })
  }

  // 留言变化
  function onMessageChange(val: string, deliveryType: number) {
    if (deliveryType === ShipType.DOMESTIC) {
      setDomesticMessage(val)
    } else {
      setOverseasMessage(val)
    }
  }

  // 校验收货地址
  function checkOrderAddress() {
    const { name, phone, area, address } = customer
    if (!name || !phone || !area?.length || !address) {
      Toast.show(t('请填写收货地址'))
      orderAddressRef.current.onOpenAddress()
      return false
    }
    return true
  }

  // 获取所有需要清空的货品
  function getAllClearProducts() {
    return [
      ...domesticProducts.map((item) => ({ saleProduct: {}, productId: item.saleProduct?.productId })),
      ...overseasProducts.map((item) => ({ saleProduct: {}, productId: item.saleProduct?.productId })),
    ]
  }

  // 获取提交给后端的物流参数
  function getLogisticsParams() {
    const { pickType: domesticPickType, logisticsId: domesticLogisticsId } = domesticShipping
    const { pickType: overseaPickType, logisticsId: overseaLogisticsId } = overseaShipping

    const params: any = {
      domesticCod: null,
      domesticPickup: null,
      overseasCod: null,
      overseasPickup: null,
    }
    if (domesticLogisticsId) {
      if (domesticPickType === PickType.COD) {
        params.domesticCod = domesticShipping
      } else {
        params.domesticPickup = domesticShipping
      }
    }

    if (overseaLogisticsId) {
      if (overseaPickType === PickType.COD) {
        params.overseasCod = overseaShipping
      } else {
        params.overseasPickup = overseaShipping
      }
    }

    for (const key in vatList) {
      const vatFeeKey = key === '0' ? 'overseasVatFee' : 'domesticVatFee'
      params[vatFeeKey] = cal.times(vatList[key], rate)
    }

    return params
  }

  // 确认订单
  async function orderConfirm() {
    if (!checkOrderAddress()) return

    const data = {
      cartId,
      corpId,
      pfUserId,
      domesticMessage,
      overseasMessage,
      ...getLogisticsParams(),
    }

    if (domesticProducts.length && (!data.domesticCod && !data.domesticPickup)) {
      Toast.show(t('请选择物流'))
      return
    }

    if (overseasProducts.length && (!data.overseasCod && !data.overseasPickup)) {
      Toast.show(t('请选择物流'))
      return
    }

    setSaveLoading(true)
    const [err, res] = await to(
      orderApi.orderAdd(data)
    )
    setSaveLoading(false)

    if (err || !res) {
      return
    }

    const resData = res.data || {}
    const { cart: resCart = {}, domesticOrder, overseasOrder } = resData
    const orderId = domesticOrder?.orderId || overseasOrder?.orderId

    const products = [...domesticProducts, ...overseasProducts].map(item => {
      const { productId: product_id, details } = item.saleProduct
      const sku_id = details
        .map((item: any) => item.skuId)
        .filter((item: any) => !!item)

      return { product_id, sku_id }
    })

    // 埋点，货品下单次数
    uploadEt({
      event_type: 7,
      products
    })

    Toast.show(t('下单成功'))
    setCart({
      ...cart,
      ...resCart,
    })
    eventUtil.emit('updateProduct', getAllClearProducts())
    navigate(`${routesMap.OrderResult}?orderId=${orderId}&amount=${summary.amount}`, { replace: true })
  }

  // 配送方式变化
  function onShippingOptionChange({ deliveryType, shippingOption }: { deliveryType: number, shippingOption: OrderPickType }) {
    if (deliveryType === ShipType.DOMESTIC) {
      setDomesticShipping(shippingOption || {})
    } else {
      setOverseaShipping(shippingOption || {})
    }
  }

  // 运费变化后，更新汇总的费用数据
  function handleFeeChange() {
    const { shippingFee: shippingFee1, codFee: codFee1 } = domesticShipping
    const { shippingFee: shippingFee2, codFee: codFee2 } = overseaShipping
    summary.shippingFee = (domesticProducts.length > 0 ? (shippingFee1 || 0) : 0) +
      (overseasProducts.length > 0 ? (shippingFee2 || 0) : 0)
    summary.codFee = (domesticProducts.length > 0 ? (codFee1 || 0) : 0) +
      (overseasProducts.length > 0 ? (codFee2 || 0) : 0)
    summary.vatTotal = cal.times(Object.values(vatList).reduce((per, cur) => cal.plus(cur, per), 0), rate)
    summary.total = cal.plus(summary.amount, summary.shippingFee, summary.codFee, summary.vatTotal)

    setSummary({ ...summary })
  }

  function onShippingOptionClick(logisticsId: string, deliveryType: number, orderAmount: number, crossBorder: number) {
    navigate(routesMap.ShippingTemplate, { state: { logisticsId, deliveryType, orderAmount, crossBorder } })
  }

  function handleVatCheckbox(isCheckbox: boolean, deliveryType: number | null, orderAmount: number) {
    const activeDeliveryType = deliveryType || 0
    if (isCheckbox) {
      vatList[activeDeliveryType] = orderAmount
    } else {
      delete vatList[activeDeliveryType]
    }

    setVatList({ ...vatList })
  }

  // 获取订单的视图
  function getOrderView(deliveryType: number) {
    const products =
      deliveryType === ShipType.DOMESTIC ? domesticProducts : overseasProducts
    const orderAmount =
      deliveryType === ShipType.DOMESTIC ? domesticsAmount : overseasAmount
    const remark =
      deliveryType === ShipType.DOMESTIC ? domesticMessage : overseasMessage
    const shippingOption = (deliveryType === ShipType.DOMESTIC ? domesticShipping : overseaShipping) || {}
    const { logisticsId } = shippingOption
    const crossBorder = deliveryType === ShipType.DOMESTIC ? 0 : 1 // 是否跨境运输 0:否 1:是

    return (
      <>
        {!!products?.length && (
          <>
            <div className={styles['order-section']}>
              <div className={styles['order-header']}>
                <div className={styles['header-left']}>
                  <div className={styles['total-label']}>
                    {t('共')}
                    （{products.length} {t('种')}）
                  </div>
                  <ShippingTypeLabel shippingType={deliveryType} styleType="default" />
                </div>
                <div className={styles['money']}>
                  <span>{currencySymbol}</span>
                  {moneyFormat(orderAmount)}

                  {/* 暂时隐藏VAT */}
                  {SHOW_VAT ? (<div className={styles['checkbox-wrap']}>
                    <Checkbox value={orderAmount} style={{ '--font-size': px2rem(24), '--icon-size': px2rem(34) }} onChange={(val) => handleVatCheckbox(val, deliveryType, orderAmount)}>
                      <span className={styles['checkbox-text']}>VAT</span>
                      {
                        isNumber(vatList[deliveryType]) && (
                          <span className={styles['checkbox-value']}>{currencySymbol}{moneyFormat(cal.times(orderAmount, rate), 2)}</span>
                        )
                      }
                    </Checkbox>
                  </div>) : null}
                </div>
              </div>

              <div className={styles['product-list']}>
                {products.map((product, index) => (
                  <ConfirmProductItem
                    product={product}
                    key={index}
                    currencySymbol={currencySymbol}
                    isGoToDetail={false}
                  />
                ))}
              </div>

              <ShippingOption
                shippingOption={shippingOption}
                currencySymbol={currencySymbol}
                onClick={() => onShippingOptionClick(logisticsId, deliveryType, orderAmount, crossBorder)}
              />

              <div className={styles['order-message']}>
                <div className={styles['order-message-label']}>{t('留言')}</div>
                <Input
                  className={styles['order-message-input']}
                  value={remark}
                  placeholder={t('给订单留个言~')}
                  maxLength={400}
                  onChange={(val) => onMessageChange(val, deliveryType)}
                />
              </div>
            </div>

            <Gap height="8px" />
          </>
        )}
      </>
    )
  }

  return (
    <div className={styles['confirm-order']}>
      <NavBar children={t('结账')} onBack={() => navigate(-1)} />
      <OrderAddress ref={orderAddressRef} pfUserId={pfUserId} onSave={setCustomer} />
      <Gap />

      <div className={styles['order-container']}>
        {getOrderView(ShipType.DOMESTIC)}
        {getOrderView(ShipType.OVER_SEA)}

        <div className={styles['summary-detail']}>
          <div className={styles['summary-detail-item']}>
            <div>{t('货款')}</div>
            <div>
              {' '}
              {currencySymbol}
              {moneyFormat(summary.amount)}
            </div>
          </div>
          {/* <div className={styles['summary-detail-item']}>
            <div>{t('运费')}</div>
            <div>
              {currencySymbol}
              {moneyFormat(summary.shippingFee)}
            </div>
          </div> */}
          {!!summary.codFee && !isAllSelfPick && (
            <div className={styles['summary-detail-item']}>
              <div>{t('COD服务费')}</div>
              <div>
                {currencySymbol}
                {moneyFormat(summary.codFee)}
              </div>
            </div>
          )}
          {/* {
            summary.vatTotal > 0 && (
              <div className={styles['summary-detail-item']}>
                <div>{t('VAT')}</div>
                <div>
                  {currencySymbol}
                  {moneyFormat(summary.vatTotal)}
                </div>
              </div>
            )
          } */}

          <div className={styles['summary-detail-total']}>
            {t('合计：')}
            {currencySymbol}
            {moneyFormat(summary.total)}
          </div>
        </div>
      </div>

      <Gap height="8px" />

      <div className={styles['bottom-section']}>
        <div className={styles['tip']}>
          {t('点击“下单”即表示您同意')}

          <div className={styles['link-tip']}>{t('Epaifa的交易条款')}</div>
        </div>
        <div className={styles['bottom-box']}>
          <div className={styles['bottom-total']}>
            <div>
              <span className={styles['total-text']}>{t('合计：')}</span>
              <span className={styles['total-money']}>
                {currencySymbol}
                {moneyFormat(summary.total)}
              </span>
            </div>
          </div>
          <Button
            color="primary"
            className={styles['btn-primary']}
            loading={saveLoading}
            onClick={orderConfirm}
          >
            {t('下单')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ConfirmOrder
